import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Logo from '../../components/Logo';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicWaitlistPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [practice, setPractice] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    preferred_time: 'egal',
    channel: 'auto',
    note: '',
    consent_given: false,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    axios
      .get(`${BASE}/api/public/practices/${slug}`)
      .then((r) => setPractice(r.data.practice))
      .catch(() => setNotFound(true));
  }, [slug]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); }

  function validate() {
    const e = {};
    if (!form.first_name.trim()) e.first_name = t('publicPage.form.firstName');
    if (!form.last_name.trim()) e.last_name = t('publicPage.form.lastName');
    if (!/^\+\d{8,15}$/.test(form.phone.trim())) e.phone = t('publicPage.form.errorPhone');
    if (!form.consent_given) e.consent_given = t('publicPage.form.errorConsent');
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${BASE}/api/public/practices/${slug}/waitlist`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        preferred_time: form.preferred_time,
        channel: form.channel,
        note: form.note.trim(),
        consent_given: true,
      });
      setSuccess(true);
    } catch (err) {
      const details = err.response?.data?.details;
      if (details) setErrors(Object.fromEntries(Object.entries(details).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])));
    } finally {
      setSubmitting(false);
    }
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="public-not-found">
        <div className="so-card max-w-md p-8 text-center">
          <h1 className="text-xl font-bold">{t('publicPage.notFound.title')}</h1>
          <p className="mt-2 text-sm text-text-secondary">{t('publicPage.notFound.text')}</p>
        </div>
      </div>
    );
  }
  if (!practice) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-text-secondary text-sm">{t('common.loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="public-waitlist-page">
      <header className="px-6 md:px-10 py-5 border-b border-border bg-white">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          {practice.logo_url ? (
            <img src={practice.logo_url} alt={practice.name} className="h-10 w-10 rounded-md object-cover border border-border" />
          ) : (
            <Logo variant="dark" />
          )}
          <div>
            <h1 className="text-base font-bold text-navy" data-testid="public-practice-name">{practice.name}</h1>
            {practice.address && <p className="text-[11px] text-text-secondary">{practice.address}</p>}
          </div>
        </div>
      </header>

      <main className="flex-1 px-6 py-10">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-text" data-testid="public-hero">{t('publicPage.hero')}</h2>
          <p className="mt-2 text-text-secondary">{practice.description || t('publicPage.subhero')}</p>

          {success ? (
            <div className="so-card mt-8 p-8 text-center" data-testid="public-success">
              <div className="mx-auto w-12 h-12 rounded-full bg-success/10 text-success flex items-center justify-center text-2xl">✓</div>
              <h3 className="mt-4 text-lg font-bold">{t('publicPage.form.successTitle')}</h3>
              <p className="mt-2 text-sm text-text-secondary">{t('publicPage.form.successText')}</p>
            </div>
          ) : (
            <form className="so-card mt-8 p-6 md:p-8 space-y-4" onSubmit={submit} noValidate data-testid="public-form">
              <h3 className="text-sm font-semibold">{t('publicPage.form.title')}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="so-label">{t('publicPage.form.firstName')}</label>
                  <input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className="so-input" data-testid="public-first-name" />
                </div>
                <div>
                  <label className="so-label">{t('publicPage.form.lastName')}</label>
                  <input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className="so-input" data-testid="public-last-name" />
                </div>
              </div>
              <div>
                <label className="so-label">{t('publicPage.form.phone')}</label>
                <input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+43..." className="so-input font-mono" data-testid="public-phone" />
                {errors.phone && <p className="mt-1 text-xs text-danger" data-testid="public-phone-error">{errors.phone}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="so-label">{t('publicPage.form.preferredTime')}</label>
                  <select value={form.preferred_time} onChange={(e) => set('preferred_time', e.target.value)} className="so-input" data-testid="public-preferred-time">
                    <option value="vormittag">{t('waitlist.form.preferredTimes.vormittag')}</option>
                    <option value="nachmittag">{t('waitlist.form.preferredTimes.nachmittag')}</option>
                    <option value="abend">{t('waitlist.form.preferredTimes.abend')}</option>
                    <option value="egal">{t('waitlist.form.preferredTimes.egal')}</option>
                  </select>
                </div>
                <div>
                  <label className="so-label">{t('publicPage.form.channel')}</label>
                  <select value={form.channel} onChange={(e) => set('channel', e.target.value)} className="so-input" data-testid="public-channel">
                    <option value="auto">{t('common.channelOptions.auto')}</option>
                    <option value="whatsapp">{t('common.channelOptions.whatsapp')}</option>
                    <option value="sms">{t('common.channelOptions.sms')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="so-label">{t('publicPage.form.note')}</label>
                <textarea value={form.note} onChange={(e) => set('note', e.target.value)} rows={2} className="so-input" data-testid="public-note" />
              </div>

              <label className="flex items-start gap-3 rounded-md border border-border bg-background/60 p-3 cursor-pointer" data-testid="public-consent-label">
                <input
                  type="checkbox"
                  checked={form.consent_given}
                  onChange={(e) => set('consent_given', e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border text-accent focus:ring-accent"
                  data-testid="public-consent-checkbox"
                />
                <span className="text-sm">
                  {t('publicPage.form.consentLabel')}{' '}
                  <Link to={`/datenschutz/${slug}`} target="_blank" rel="noreferrer" className="so-link" data-testid="public-datenschutz-inline">
                    {t('publicPage.form.consentLink')}
                  </Link>
                  {t('publicPage.form.consentSuffix')}
                </span>
              </label>
              {errors.consent_given && <p className="text-xs text-danger" data-testid="public-consent-error">{errors.consent_given}</p>}

              <button type="submit" disabled={submitting} className="so-btn so-btn-primary w-full" data-testid="public-submit">
                {submitting ? t('publicPage.form.submitting') : t('publicPage.form.submit')}
              </button>
            </form>
          )}
        </div>
      </main>

      <footer className="border-t border-border bg-white px-6 py-5" data-testid="public-footer">
        <div className="max-w-2xl mx-auto flex flex-wrap items-center justify-between gap-3 text-xs text-text-secondary">
          <div className="flex items-center gap-4">
            <Link to={`/impressum/${slug}`} className="so-link" data-testid="public-impressum-link">{t('publicPage.footer.impressum')}</Link>
            <Link to={`/datenschutz/${slug}`} className="so-link" data-testid="public-datenschutz-link">{t('publicPage.footer.datenschutz')}</Link>
          </div>
          <p>{t('publicPage.footer.powered')}</p>
        </div>
      </footer>
    </div>
  );
}
